import React, { createContext, useContext, useMemo } from "react";
import { useMatches } from "react-router-dom";

import { type NavigationConfig, useNavigationConfig } from "../../api/hooks/defs/navigation";

import type { MainNavItemConfig, NavItemConfig } from "./mainNavConfig";
import { calculateMainNavConfig, findNavItemByPath } from "./mainNavConfig";

interface NavigationContextValue {
	// Raw config from API (from TanStack Query)
	config: NavigationConfig | null;
	isLoading: boolean;

	// Computed navigation structure
	mainNavItems: MainNavItemConfig[];

	// Current navigation state
	navPath: (string | null | undefined)[];
	activeMainTab: string | null;
	activeNavItem: NavItemConfig | null;
	parentNavItem: NavItemConfig | null;
	currentSubNavItems: NavItemConfig[];

	// Helper functions
	getChildrenByPath: (path: string[]) => NavItemConfig[];
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { data: navData, isLoading: navIsLoading } = useNavigationConfig();
	const matches = useMatches();

	// Extract navPath from route
	const navPath = useMemo(() => {
		// Check if current URL contains administration routes
		const currentPath = matches[matches.length - 1]?.pathname || "";
		if (currentPath.includes("/administration") || currentPath.includes("/super-user-admin")) {
			return ["administration"];
		}

		// Find the most specific route match with a navPath
		for (let i = matches.length - 1; i >= 0; i--) {
			const handle = matches[i].handle as { navPath?: (string | null | undefined)[] } | undefined;
			if (handle?.navPath && handle.navPath.length > 0 && handle.navPath[0]) {
				return handle.navPath;
			}
		}
		return [];
	}, [matches]);

	// Compute navigation structure from config
	const mainNavItems = useMemo(() => {
		if (!navData?.navigationConfig) return [];
		return calculateMainNavConfig(navData.navigationConfig, navData.navigationConfig.theUserId);
	}, [navData]);

	// Compute active navigation state
	const { activeMainTab, activeNavItem, parentNavItem, currentSubNavItems } = useMemo(() => {
		const activeMainTab = (navPath[0] as string | null) || null;

		// Find the active nav item at the deepest level
		const activeNavItem = navPath.length > 0 ? findNavItemByPath(mainNavItems, navPath) : null;

		// Find the parent nav item (one level up from active)
		const parentNavItem = navPath.length > 1 ? findNavItemByPath(mainNavItems, navPath.slice(0, -1)) : null;

		// Extract sub-nav items from parent's children
		const currentSubNavItems = parentNavItem?.children || [];

		return { activeMainTab, activeNavItem, parentNavItem, currentSubNavItems };
	}, [navPath, mainNavItems]);

	// Helper function to get children by path
	const getChildrenByPath = useMemo(
		() => (path: string[]) => {
			const item = findNavItemByPath(mainNavItems, path);
			return item?.children || [];
		},
		[mainNavItems]
	);

	const value = useMemo(
		() => ({
			config: navData?.navigationConfig || null,
			isLoading: navIsLoading,
			mainNavItems,
			navPath,
			activeMainTab,
			activeNavItem,
			parentNavItem,
			currentSubNavItems,
			getChildrenByPath,
		}),
		[
			navData,
			navIsLoading,
			mainNavItems,
			navPath,
			activeMainTab,
			activeNavItem,
			parentNavItem,
			currentSubNavItems,
			getChildrenByPath,
		]
	);

	return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
	const context = useContext(NavigationContext);
	if (context === undefined) {
		throw new Error("useNavigation must be used within a NavigationProvider");
	}
	return context;
};
